import { useState } from "react";
import { useEsAdmin } from "@/hooks/useEsAdmin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { AlertCircle, Pencil, X } from "lucide-react";
import { api } from "@/lib/api";
import {
  DetailLayout,
  DetailLoading,
  DetailError,
  Section,
  Field,
  StatusBadge,
  formatDate,
} from "@/components/DetailLayout";
import {
  RepresentanteFormFields,
  normalizarRepresentante,
  validarRepresentante,
} from "@/components/RepresentanteFormFields";
import type { RepresentanteBorrador } from "@/pages/mi-registro-legal/types";
import { useTieneRol } from "@/lib/sesion";

interface RepDetalle {
  id: string;
  registroLegalId: string;
  nombreCompleto: string;
  nombres: string | null;
  apellidoPaterno: string | null;
  apellidoMaterno: string | null;
  correo: string | null;
  rfc: string | null;
  curp: string | null;
  telefono: string | null;
  telefonoExtension: string | null;
  domicilioCalle: string | null;
  domicilioNumeroExterior: string | null;
  domicilioNumeroInterior: string | null;
  domicilioCodigoPostal: string | null;
  domicilioColonia: string | null;
  domicilioMunicipio: string | null;
  domicilioEntidadFederativa: string | null;
  domicilioPais: string | null;
  domicilioLibre: string | null;
  consentimientoNotificacionElectronica: boolean;
  instrumentoPoderDescripcion: string | null;
  usuarioId: string | null;
  instrumentoPoderDocumentoId: string | null;
  instrumentoPoderNombreArchivo: string | null;
  estatus: string;
  vigenciaInicio: string;
  vigenciaFin: string | null;
}

const ROLES_CALIDAD = new Set(["GERENTE_CALIDAD", "AUXILIAR_CALIDAD", "ADMIN_SICEN"]);

function tonePorEstatus(estatus: string): "ok" | "info" | "warn" | "neutral" {
  if (estatus === "ACTIVO") return "ok";
  if (estatus === "REVOCADO" || estatus === "VENCIDO") return "warn";
  return "info";
}

function tieneDomicilioEstructurado(r: RepDetalle): boolean {
  return Boolean(
    r.domicilioCalle ||
      r.domicilioColonia ||
      r.domicilioMunicipio ||
      r.domicilioEntidadFederativa ||
      r.domicilioCodigoPostal,
  );
}

export function RepresentanteLegalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const esAdmin = useEsAdmin();
  const usuarioEsCalidad = useTieneRol(...ROLES_CALIDAD);
  const [edicionAbierta, setEdicionAbierta] = useState(false);

  const q = useQuery({
    queryKey: ["representante-legal", id],
    queryFn: async () => (await api.get<RepDetalle>(`/api/representantes-legales/${id}`)).data,
    enabled: !!id,
  });

  if (q.isLoading) return <DetailLoading />;
  if (q.error || !q.data) return <DetailError />;
  const r = q.data;
  const tieneEstructurado = tieneDomicilioEstructurado(r);

  return (
    <DetailLayout
      title={r.nombreCompleto}
      subtitle={
        <>
          Representante legal de{" "}
          <Link to={`/registros-legales/${r.registroLegalId}`} className="hover:underline">
            ver registro
          </Link>
        </>
      }
      backTo="/tramites-legales"
      backLabel="Volver"
      badges={
        <>
          <StatusBadge tone={tonePorEstatus(r.estatus)}>{r.estatus}</StatusBadge>
          {!r.instrumentoPoderDocumentoId && <StatusBadge tone="warn">sin instrumento</StatusBadge>}
          {usuarioEsCalidad && !edicionAbierta && (
            <button
              onClick={() => setEdicionAbierta(true)}
              className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-2.5 py-0.5 text-xs font-medium text-primary-strong transition hover:bg-primary-tint"
            >
              <Pencil className="h-3 w-3" /> Editar
            </button>
          )}
        </>
      }
      rawData={r}
    >
      <Section title="Identidad">
        <Field label="Nombre(s)" value={r.nombres} />
        <Field label="Apellido paterno" value={r.apellidoPaterno} />
        <Field label="Apellido materno" value={r.apellidoMaterno} />
        <Field label="Nombre completo" value={r.nombreCompleto} span={3} />
        <Field label="RFC" value={r.rfc} mono />
        <Field label="CURP" value={r.curp} mono />
        <Field label="Correo" value={r.correo} />
      </Section>

      <Section title="Contacto">
        <Field label="Teléfono" value={r.telefono} />
        <Field label="Extensión" value={r.telefonoExtension} />
        <Field
          label="Consentimiento de notificación"
          value={r.consentimientoNotificacionElectronica ? "Otorgado" : "No otorgado"}
        />
      </Section>

      <Section title="Domicilio">
        {tieneEstructurado ? (
          <>
            <Field label="Código postal" value={r.domicilioCodigoPostal} mono />
            <Field label="Entidad federativa" value={r.domicilioEntidadFederativa} />
            <Field label="Municipio / Alcaldía" value={r.domicilioMunicipio} />
            <Field label="Colonia" value={r.domicilioColonia} span={3} />
            <Field label="Calle" value={r.domicilioCalle} span={3} />
            <Field label="Número exterior" value={r.domicilioNumeroExterior} />
            <Field label="Número interior" value={r.domicilioNumeroInterior} />
            <Field label="País" value={r.domicilioPais || "MEX"} />
          </>
        ) : (
          <Field label="Domicilio (legacy)" value={r.domicilioLibre} span={3} />
        )}
      </Section>

      <Section title="Vínculo con usuario SICEN">
        <Field label="Usuario asociado" value={r.usuarioId} mono span={3} />
        <Field
          label="Registro legal"
          span={3}
          value={
            <Link to={`/registros-legales/${r.registroLegalId}`} className="text-primary-strong hover:underline">
              {r.registroLegalId}
            </Link>
          }
        />
      </Section>

      <Section title="Vigencia">
        <Field label="Vigencia inicio" value={formatDate(r.vigenciaInicio)} />
        <Field label="Vigencia fin" value={formatDate(r.vigenciaFin)} />
        <Field label="Estatus" value={<StatusBadge tone={tonePorEstatus(r.estatus)}>{r.estatus}</StatusBadge>} />
      </Section>

      <Section title="Instrumento de poder">
        <Field label="Descripción" value={r.instrumentoPoderDescripcion} span={3} />
        <Field label="Documento ID" value={r.instrumentoPoderDocumentoId} mono span={2} />
        <Field
          label="Archivo"
          value={
            r.instrumentoPoderDocumentoId
              ? r.instrumentoPoderNombreArchivo ?? "—"
              : <StatusBadge tone="warn">sin instrumento vinculado</StatusBadge>
          }
        />
      </Section>

      {/* ID interno: solo para ADMIN_SICEN (soporte); confunde al usuario final */}
      {esAdmin && (
        <Section title="Metadatos">
          <Field label="ID" value={r.id} mono span={3} />
        </Section>
      )}

      {edicionAbierta && (
        <EdicionRepresentanteModal r={r} onClose={() => setEdicionAbierta(false)} />
      )}
    </DetailLayout>
  );
}

function repDetalleAFormulario(r: RepDetalle): RepresentanteBorrador {
  return {
    nombres: r.nombres ?? "",
    apellidoPaterno: r.apellidoPaterno ?? "",
    apellidoMaterno: r.apellidoMaterno ?? "",
    nombreCompleto: r.nombreCompleto ?? "",
    rfc: r.rfc ?? "",
    curp: r.curp ?? "",
    correo: r.correo ?? "",
    telefono: r.telefono ?? "",
    telefonoExtension: r.telefonoExtension ?? "",
    domicilioCalle: r.domicilioCalle ?? "",
    domicilioNumeroExterior: r.domicilioNumeroExterior ?? "",
    domicilioNumeroInterior: r.domicilioNumeroInterior ?? "",
    domicilioCodigoPostal: r.domicilioCodigoPostal ?? "",
    domicilioColonia: r.domicilioColonia ?? "",
    domicilioMunicipio: r.domicilioMunicipio ?? "",
    domicilioEntidadFederativa: r.domicilioEntidadFederativa ?? "",
    domicilioPais: r.domicilioPais ?? "MEX",
    domicilioLibre: r.domicilioLibre ?? "",
    consentimientoNotificacionElectronica: r.consentimientoNotificacionElectronica,
    instrumentoPoderDescripcion: r.instrumentoPoderDescripcion ?? "",
    vigenciaInicio: r.vigenciaInicio,
    vigenciaFin: r.vigenciaFin,
  };
}

function EdicionRepresentanteModal({ r, onClose }: { r: RepDetalle; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<RepresentanteBorrador>(repDetalleAFormulario(r));
  const [motivo, setMotivo] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      const normalizado = normalizarRepresentante(form);
      await api.put(`/api/representantes-legales/${r.id}`, {
        motivo: motivo.trim(),
        ...normalizado,
        consentimientoNotificacionElectronica: form.consentimientoNotificacionElectronica,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["representante-legal", r.id] });
      onClose();
    },
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data;
      setErr(data?.detail || data?.title || (e as Error).message);
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!motivo.trim()) {
      setErr("Captura un motivo del cambio (queda en bitácora).");
      return;
    }
    const validacion = validarRepresentante(form);
    if (validacion) {
      setErr(validacion);
      return;
    }
    setErr(null);
    mut.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-surface shadow-overlay">
        <div className="sticky top-0 flex items-center justify-between border-b bg-surface px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">Editar representante legal</h3>
          <button type="button" onClick={onClose} className="cursor-pointer rounded p-1 transition hover:bg-surface-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-5 p-4">
          <div className="rounded-lg border border-estado-warn/25 bg-estado-warn-bg p-3">
            <label className="block text-xs font-semibold uppercase tracking-wider text-estado-warn">
              Motivo del cambio *
            </label>
            <input
              type="text"
              required
              maxLength={500}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej.: Completar datos faltantes solicitados por NOM-008"
              className="mt-1 w-full rounded border border-estado-warn/25 bg-surface px-2 py-1.5 text-sm"
            />
            <p className="mt-1 text-xs text-estado-warn">El motivo queda en bitácora junto con el delta de cambios.</p>
          </div>

          <RepresentanteFormFields r={form} setR={setForm} />

          {err && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer rounded border px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="cursor-pointer rounded bg-primary px-4 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
            >
              {mut.isPending ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
